import type { PlayerStatsRow } from '@api-service/player-stats';
import { getByPath } from '@lib/page-utils/Opta/path';

export type PlayerPosition = 'GK' | 'DEF' | 'MID' | 'FWD';

/**
 * Reads a dot-path off a stats row as a number, distinguishing "the API did not
 * return this" from "the API returned zero". The current page collapses both to
 * `0`, which is why a player with no minutes renders `0.00` across every panel
 * instead of saying it has nothing to show.
 */
export const readStat = (
  row: PlayerStatsRow | undefined,
  path: string
): number | null => {
  if (!row) return null;
  const value = getByPath(row, path);
  return typeof value === 'number' ? value : null;
};

/**
 * First season carrying raw Opta event counts (shots, defensive actions, set
 * pieces, involvement). Earlier seasons hold only derived metrics — expected
 * goals, expected assists, goals, assists — plus FPL data, so count-based
 * modules must be hidden rather than rendered as zeroes.
 *
 * Mirrors `OPTA_DETAILED_STATS_FROM_SEASON` in `@lib/page-utils/Opta/columns`.
 */
export const DETAILED_STATS_FROM_SEASON = 2025;

/**
 * First season with a real `fpl.expectedPoints` figure. Earlier seasons return
 * 0 rather than null for every player, so an xPts axis or column before this
 * reads as "worst in the league" when it is actually "not measured".
 */
export const EXPECTED_POINTS_FROM_SEASON = 2025;

/**
 * First season FPL awarded defensive contribution points. Matches
 * `DEF_CON_ENABLED_SEASON` in the API's scoring library, which gates the
 * `fpl.defensiveContribution.*` fields the module reads.
 */
export const DEFENSIVE_CONTRIBUTION_FROM_SEASON = 2025;

/** Points FPL awards for clearing the defensive contribution threshold. */
export const DEFENSIVE_CONTRIBUTION_POINTS_ON_HIT = 2;

/**
 * Minutes a player must have logged to enter the percentile population. Below
 * this a single good match distorts a per-90 rate badly enough that the rank
 * misleads more than it informs.
 */
export const PERCENTILE_MINIMUM_MINUTES = 450;

/**
 * Defensive contribution is scored on a different action set and a different
 * threshold depending on position, which the API's scoring library treats as
 * two distinct rules rather than one configurable number:
 *
 * - Defenders are measured on clearances, blocks, interceptions and tackles
 *   (CBIT) against a threshold of 10. Ball recoveries do not count.
 * - Midfielders add ball recoveries (CBIT+R) against 12. In FPL's own
 *   scoring forwards share this rule, but this module never shows it for
 *   them (see below), so they get no rule here.
 * - Goalkeepers never score defensive contribution at all.
 *
 * Forwards do technically score it in FPL, but they clear the threshold so
 * rarely that the module reads as noise on their profiles, so the product
 * choice is to not show it for them at all.
 */
type DefensiveContributionRule = {
  /** Stats path holding the qualifying action tally for this position. */
  statPath: string;
  /**
   * The matching key on a history row's `stats.defensiveActions`. Named
   * separately from `statPath` because the aggregate stats endpoint and the
   * history payload nest the same two tallies differently.
   */
  historyKey: 'cbit' | 'cbitr';
  /** Actions needed in a single match to bank the points. */
  threshold: number;
  /** Short label for the metric, used on gauges and axis legends. */
  metricLabel: string;
  /** Sentence describing the rule, shown under the module title. */
  ruleDescription: string;
};

const DEFENDER_RULE: DefensiveContributionRule = {
  statPath: 'defensiveActions.cbit',
  historyKey: 'cbit',
  threshold: 10,
  metricLabel: 'CBIT',
  ruleDescription:
    'Needs 10 or more clearances, blocks, interceptions and tackles in a match to bank 2 points. Ball recoveries do not count for defenders.'
};

const MIDFIELDER_RULE: DefensiveContributionRule = {
  statPath: 'defensiveActions.cbitr',
  historyKey: 'cbitr',
  threshold: 12,
  metricLabel: 'CBIT+R',
  ruleDescription:
    'Needs 12 or more clearances, blocks, interceptions, tackles and recoveries in a match to bank 2 points.'
};

export const getDefensiveContributionRule = (
  position: PlayerPosition
): DefensiveContributionRule | null => {
  if (position === 'GK' || position === 'FWD') return null;
  return position === 'DEF' ? DEFENDER_RULE : MIDFIELDER_RULE;
};

/**
 * How reliably a player banks defensive contribution points, derived from the
 * share of appearances that cleared the threshold. Drives the status chip on
 * the module header so the headline reads before the numbers do.
 */
export type DefensiveContributionStanding =
  | 'reliable'
  | 'occasional'
  | 'unlikely';

const RELIABLE_HIT_RATE = 0.6;
const OCCASIONAL_HIT_RATE = 0.25;

export const getDefensiveContributionStanding = (
  hitRate: number
): DefensiveContributionStanding => {
  if (hitRate >= RELIABLE_HIT_RATE) return 'reliable';
  if (hitRate >= OCCASIONAL_HIT_RATE) return 'occasional';
  return 'unlikely';
};

export const DEFENSIVE_CONTRIBUTION_STANDING_LABEL: Record<
  DefensiveContributionStanding,
  string
> = {
  reliable: 'Reliable source',
  occasional: 'Occasional source',
  unlikely: 'Rarely returns'
};

/**
 * Which matches the hit rate is measured over. Starts is the default: a player
 * who comes off the bench has little chance of clearing the threshold, so
 * counting cameos understates a regular starter and a handful of them can
 * flatter a fringe player.
 */
export type DefensiveContributionBasis = 'starts' | 'appearances';

export const DEFENSIVE_CONTRIBUTION_BASIS_OPTIONS: {
  value: DefensiveContributionBasis;
  label: string;
}[] = [
  { value: 'starts', label: 'Starts' },
  { value: 'appearances', label: 'Appearances' }
];

/**
 * Below this many counted matches a single match moves the rate too far for it
 * to be read as a rate at all, so the module qualifies the number rather than
 * presenting it alongside a standing chip.
 */
export const HIT_RATE_MINIMUM_SAMPLE = 5;

export type DefensiveContributionGameweek = {
  gameweek: number;
  /** Qualifying actions in that gameweek. Zero for a gameweek not played. */
  actions: number;
  didStart: boolean;
  /**
   * Whether the player was on the pitch at all. The history endpoint returns a
   * zeroed row for a gameweek the player's team played but they did not
   * feature in, so a zero action count alone cannot tell a blank apart from a
   * quiet appearance.
   */
  hasAppeared: boolean;
};

export type DefensiveContributionSummary = {
  /** Null when nothing was counted, which is not the same as a rate of zero. */
  hitRate: number | null;
  clearedCount: number;
  countedCount: number;
  hasSmallSample: boolean;
};

export const summariseDefensiveContribution = ({
  gameweeks,
  threshold,
  basis
}: {
  gameweeks: DefensiveContributionGameweek[];
  threshold: number;
  basis: DefensiveContributionBasis;
}): DefensiveContributionSummary => {
  const counted = gameweeks.filter((gameweek) =>
    basis === 'starts' ? gameweek.didStart : gameweek.hasAppeared
  );
  const clearedCount = counted.filter(
    (gameweek) => gameweek.actions >= threshold
  ).length;

  return {
    hitRate: counted.length > 0 ? clearedCount / counted.length : null,
    clearedCount,
    countedCount: counted.length,
    hasSmallSample:
      counted.length > 0 && counted.length < HIT_RATE_MINIMUM_SAMPLE
  };
};

/** A row in the attacking threat module. */
export type ThreatMetric = {
  label: string;
  note?: string;
  statPath: string;
  /** Metrics unavailable before detailed Opta data starts. */
  needsDetailedStats: boolean;
};

export const ATTACKING_THREAT_METRICS: ThreatMetric[] = [
  {
    label: 'Expected goals',
    note: 'Excluding penalties',
    statPath: 'shooting.nonPenaltyExpectedGoals',
    needsDetailedStats: false
  },
  {
    label: 'Goals',
    statPath: 'shooting.goals.total',
    needsDetailedStats: false
  },
  {
    label: 'Shots',
    statPath: 'shooting.shots.total',
    needsDetailedStats: true
  },
  {
    label: 'Shots in the box',
    statPath: 'shooting.shots.inBox',
    needsDetailedStats: true
  },
  {
    label: 'Big chances',
    statPath: 'shooting.bigChances',
    needsDetailedStats: true
  },
  {
    label: 'Key passes',
    statPath: 'creativity.keyPasses',
    needsDetailedStats: true
  }
];

export const DEFENDING_METRICS: ThreatMetric[] = [
  {
    label: 'Clean sheets',
    statPath: 'defending.cleanSheets',
    needsDetailedStats: false
  },
  {
    label: 'Expected clean sheets',
    statPath: 'defending.expectedCleanSheets',
    needsDetailedStats: false
  },
  {
    label: 'Expected goals conceded',
    note: 'Lower is better',
    statPath: 'defending.expectedGoalsConceded',
    needsDetailedStats: false
  },
  {
    label: 'Big chances conceded',
    note: 'Lower is better',
    statPath: 'defending.bigChancesConceded',
    needsDetailedStats: true
  },
  {
    label: 'Shots conceded',
    note: 'Lower is better',
    statPath: 'defending.shotsConceded.total',
    needsDetailedStats: true
  }
];

const GOALKEEPER_METRIC: ThreatMetric = {
  label: 'Saves',
  statPath: 'fpl.saves',
  needsDetailedStats: false
};

/**
 * Metrics where a higher value is worse, so the percentile has to be inverted
 * before it is coloured or labelled.
 */
export const LOWER_IS_BETTER_STAT_PATHS = new Set<string>([
  'defending.expectedGoalsConceded',
  'defending.bigChancesConceded',
  'defending.shotsConceded.total'
]);

/**
 * Which underlying-numbers module a position gets. Attacking threat is
 * meaningless for goalkeepers and close to it for defenders, and the defending
 * equivalent is noise for midfielders and forwards.
 */
export const getUnderlyingMetrics = (
  position: PlayerPosition
): { title: string; subtitle: string; metrics: ThreatMetric[] } => {
  if (position === 'GK') {
    return {
      title: 'Shot stopping and defence',
      subtitle: 'Clean sheets, expected goals conceded and saves',
      metrics: [...DEFENDING_METRICS, GOALKEEPER_METRIC]
    };
  }

  if (position === 'DEF') {
    return {
      title: 'Defending',
      subtitle: 'Clean sheets and what the defence concedes',
      metrics: DEFENDING_METRICS
    };
  }

  return {
    title: 'Attacking threat',
    subtitle: 'Chance creation and goal threat',
    metrics: ATTACKING_THREAT_METRICS
  };
};

/**
 * Forwards can bank defensive contribution points but almost never do, so the
 * module renders collapsed for them rather than as a headline panel.
 */
export const isDefensiveContributionHeadline = (
  position: PlayerPosition
): boolean => position === 'DEF' || position === 'MID';
